import { FC, useState } from "react";
import { Pagination } from "@mantine/core";
import { useDebounce } from "react-use";
import { useTranslation } from "react-i18next";
import { usePage } from "hooks/usePage";
import { useRealtimeConnection } from "hooks/useRealTimeConnection";
import config from "config";
import PageHeader from "components/layout/page-header";
import PageWrapper from "components/layout/page-wrapper";
import TableWithCheckbox from "components/TableWithCheckbox/TableWithCheckbox";
import { useQuery } from "@apollo/client";
import { fetchHoldKavaOffers } from "graphql/queries/holdOffers";
import { useHoldOfferColumns } from "./columns";

const HoldOffersPage: FC = () => {
    const { t } = useTranslation();
    usePage(`Kava | ${t("hold_offers.page_title")}`);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

    useDebounce(
        () => {
            setDebouncedSearchValue(searchValue);
        },
        1000,
        [searchValue],
    );

    const { data, loading, error, refetch } = useQuery(fetchHoldKavaOffers, {
        variables: {
            input: {},
        },
        fetchPolicy: "network-only",
    });

    useRealtimeConnection({
        subscriptions: [
            {
                channel: config.realtime.channels.KAVA_NOTIFICATION,
                events: [config.realtime.events.BROKER_THREAD_NOTIFICATION, config.realtime.events.REINSURER_READ_THREAD],
            },
            {
                channel: config.realtime.channels.KEK_CHANNEL,
                events: [config.realtime.events.BROKER_THREAD_NOTIFICATION, config.realtime.events.REINSURER_READ_THREAD],
                pusherInstanceId: "kek",
            },
        ],
        callback: () => refetch(),
    });

    const offers = [...(data?.fetchHoldKavaOffers || [])].sort((a: any, b: any) => {
        return new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime();
    });

    const filteredOffers = offers.filter((offer: any) => {
        if (!debouncedSearchValue) return true;

        const searchLower = debouncedSearchValue.toLowerCase();
        const policyNumber =
            offer?.offer_detail?.policy_number?.toLowerCase() || "";
        const insuredBy = offer?.offer_detail?.insured_by?.toLowerCase() || "";
        const businessName =
            offer?.classofbusiness?.business_name?.toLowerCase() ||
            offer?.class_of_business?.business_name?.toLowerCase() ||
            "";

        return (
            policyNumber.includes(searchLower) ||
            insuredBy.includes(searchLower) ||
            businessName.includes(searchLower)
        );
    });

    const columns = useHoldOfferColumns(refetch);

    const totalOffers = filteredOffers.length;
    const from = totalOffers === 0 ? 0 : (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalOffers);
    const lastPage = Math.ceil(totalOffers / perPage);

    return (
        <PageWrapper>
            <PageHeader title={t("hold_offers.header")} />

            <section className="mt-8" aria-labelledby="hold-offers-heading">
                <TableWithCheckbox
                    columns={columns}
                    data={filteredOffers}
                    searchValue={searchValue}
                    search={(value: string) => setSearchValue(value)}
                    setSelected={() => {}}
                    selected={[]}
                    loading={loading}
                    error={error}
                    refresh={refetch}
                    setNumberOfRows={setPerPage}
                    numberOfRows={perPage}
                    searchPlaceholder={t("hold_offers.search_placeholder")}
                />
            </section>

            <section className="p-3 flex justify-between items-center mt-6">
                <span>
                    {t("hold_offers.showing_items", {
                        from,
                        to,
                        total: totalOffers,
                    })}
                </span>
                {lastPage > 1 && (
                    <Pagination
                        total={lastPage}
                        page={page}
                        onChange={(page) => {
                            setPage(page);
                        }}
                    />
                )}
            </section>
        </PageWrapper>
    );
};

export default HoldOffersPage;
